import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  get,
  find,
  isEmpty,
} from 'lodash';
import {
  useParams,
  useNavigate,
} from 'react-router-dom';
import {
  API,
  GlobalActions,
  OrderHelper,
  NavigationHelper,
  OrderModel,
  OrderSummaryModel,
  SegmentModel,
  Field2 as Field,
  Tag,
  TableSegments,
  VibeModal,
  VibeAlert,
  VibeTooltip,
  viCopy,
  viPlayCircleOutline,
  viPauseCircleOutline,
  viTrash,
  color,
} from 'vibeguide';
import SubNavigation from '../../../Header/SubNavigation';
import SegmentSidebar from '../../Flights/Sidebar/SegmentSidebar';
import OrderStatItem from './OrderStatItem';

function OrderDetails({
  className,
  style,
  user,
  setPanel,
}) {
  const [order, setOrder] = useState({
    data: new OrderModel(),
    summary: new OrderSummaryModel(),
    segments: [],
  });

  /**
   * Modals need their own state for the types that require a "statusReason"
   * Otherwise the state is lost if using the "confirmModal" object method
   */
  const [confirmCopy, setConfirmCopy] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [statusReason, setStatusReason] = useState('');

  const {
    id: orderId,
  } = useParams();

  const history = useNavigate();
  const qs = NavigationHelper.getParams() || {};

  /**
   * Get Order Segments
   * Include order summary segments if a new summary was fetched
   * Otherwise use the existing order summary data
   */
  const getSegments = async (summarySegments) => {
    // if user cannot view segments skip
    if (!user.can('segment.view')) {
      return [];
    }

    const params = NavigationHelper.getParams();
    const active = get(params, 'active') !== 'false';

    // get all segments so we can merge with flight summary data
    const response = await API.Order.Segment.list(
      {
        _id: orderId,
        filters: {
          active,
        },
        pageSize: -1,
      },
    );

    if (!summarySegments) {
      // no summary segments passed in, use existing summary
      summarySegments = get(order, 'summary.segments', []);
    }

    // merge segment data from the order summary with segments from the API
    const segmentItems = get(response, 'data', []).map((segment) => {
      // find the associated segment in the summary data
      const summarySegment = find(summarySegments, { _id: segment._id }) || {};

      // merge the two together
      return {
        ...new SegmentModel(segment),
        ...summarySegment,
      };
    });

    return segmentItems;
  };

  /**
   * Get Order Data
   */
  const getOrderData = async () => {
    const response = await API.Order.get(orderId);
    return new OrderModel(response);
  };

  /**
   * Get Order Summary
   */
  const getOrderSummary = async () => {
    const summaryResponse = await API.Order.summary(orderId);
    return new OrderSummaryModel(summaryResponse);
  };

  /**
   * Get the Order Details
   */
  const getOrder = async () => {
    if (!orderId) {
      console.error('Order ID not Specified');
      return;
    }

    const orderData = await getOrderData();
    const orderSummaryData = await getOrderSummary();
    const orderSegments = await getSegments(orderSummaryData.segments);

    setOrder({
      data: orderData,
      summary: orderSummaryData,
      segments: orderSegments,
    });
  };

  /**
   * When the user changes the reason textarea in the confirm modal
   */
  const onChangeStatusReason = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    setStatusReason(value);
  };

  /**
   * Copy Order
   */
  const onConfirmCopy = async () => {
    const response = await API.Order.copy({
      _id: orderId,
    });

    const success = get(response, '[0].type') === 'FLIGHT.CREATED';

    // redirect to the copied order (which is now a flight)
    if (success) {
      const copyId = get(response, '[0].documentId');
      history(`/ad-ops/flight/${copyId}`);
    }
  };

  /**
   * Cancel Copy
   */
  const onCancelCopy = () => {
    setConfirmCopy(false);
  };

  /**
   * Pause Order
   */
  const onConfirmPause = async () => {
    const response = await API.Order.pause({
      _id: orderId,
      statusReason,
    });

    const success = get(response, '[0].type') === 'ORDER.PAUSED';

    if (success) {
      setConfirmPause(false);
      setStatusReason('');
      getOrder();
    }
  };

  /**
   * Cancel Pause
   */
  const onCancelPause = () => {
    setConfirmPause(false);
    setStatusReason('');
  };

  /**
   * Resume Order
   */
  const onConfirmResume = async () => {
    const response = await API.Order.resume({
      _id: orderId,
    });

    const success = get(response, '[0].type') === 'ORDER.RESUMED';

    if (success) {
      setConfirmResume(false);
      getOrder();
    }
  };

  /**
   * Cancel Resume
   */
  const onCancelResume = () => {
    setConfirmResume(false);
  };

  /**
   * Cancel Order
   */
  const onConfirmCancel = async () => {
    const response = await API.Order.cancel({
      _id: orderId,
      statusReason,
    });

    const success = get(response, '[0].type') === 'ORDER.CANCELLED';

    if (success) {
      setConfirmCancel(false);
      setStatusReason('');
      getOrder();
    }
  };

  /**
   * Cancel "Canceling" an Order
   */
  const onCancelCancel = () => {
    setConfirmCancel(false);
    setStatusReason('');
  };

  /**
   * When the sidebar is closed
   */
  const onCloseSidebar = () => {
    const url = NavigationHelper.updateParams({
      segmentId: null,
      flightId: null,
      type: null,
    }, {
      keepPage: true,
    });

    history(url);
  };

  /**
   * Get style for the status
   */
  const getTagStyle = (status) => {
    const item = find(OrderHelper.statusList, { value: status });

    return get(item, 'tagStyle', {
      background: color.manatee16,
      color: color.manatee,
    });
  };

  /**
   * Get the Buttons to use for the Dropdown
   */
  const getMenuItems = () => {
    const orderStatus = get(order, 'data.status');

    const items = [{
      text: 'Copy',
      userCan: [
        'flight.create',
      ],
      icon: viCopy,
      color: color.violetVibe,
      onClick: () => setConfirmCopy(true),
    }];

    if (orderStatus === 'paused') {
      // Can only resume orders that have been paused
      items.push({
        text: 'Resume',
        userCan: [
          'order.pause',
        ],
        icon: viPlayCircleOutline,
        color: color.violetVibe,
        onClick: () => setConfirmResume(true),
      });
    } else if (orderStatus === 'live') {
      // Can only pause live orders
      items.push({
        text: 'Pause',
        userCan: [
          'order.pause',
        ],
        icon: viPauseCircleOutline,
        color: color.violetVibe,
        onClick: () => setConfirmPause(true),
      });
    }

    if (orderStatus !== 'cancelled' && orderStatus !== 'completed') {
      // Can only cancel orders that haven't been completed or canceled already
      items.push({
        text: 'Cancel',
        userCan: [
          'order.cancel',
        ],
        icon: viTrash,
        color: color.fireBrick,
        onClick: () => setConfirmCancel(true),
      });
    }

    return items;
  };

  const sidebar = (segmentId = null) => {
    setPanel({
      show: true,
      backdrop: false,
      width: 400,
      children: (
        <SegmentSidebar
          type="order"
          segmentId={segmentId}
          routeId={orderId}
        />
      ),
      onClose: onCloseSidebar,
    });
  };

  useEffect(() => {
    const isView = qs.segmentId;

    if (isView) {
      sidebar(qs.segmentId);
    }
  }, [qs.segmentId]);

  /**
   * When the Order ID Changes
   */
  useEffect(() => {
    getOrder();
  }, [orderId]);

  const orderData = order.data;
  const orderSummary = order.summary;
  const orderSegments = order.segments;

  // do not render until an order has been fetched
  if (!orderData._id) {
    return null;
  }

  // get the style for the status tag in the header
  const tagStyle = getTagStyle(orderData.status);

  return (
    <div
      className={classNames('OrderDetails', className)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...style,
      }}
    >
      <SubNavigation
        menuName="Order Menu"
        title={(
          <div
            style={{
              width: '100%',
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {orderSummary.warnings.length > 0 && (
              <VibeAlert
                style={{
                  marginRight: 8,
                  fontSize: 12,
                }}
                severity="warning"
                alerts={orderSummary.warnings}
                size={24}
                iconOnly
              />
            )}

            <div
              style={{
                flexGrow: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <VibeTooltip title={orderData.name}>
                <span>
                  {orderData.name}
                </span>
              </VibeTooltip>
            </div>

            <VibeTooltip title={orderData.statusReason || ''}>
              <div>
                <Tag
                  tag={{
                    name: orderData.status,
                  }}
                  style={{
                    background: tagStyle.background,
                    color: tagStyle.color,
                    fontSize: 12,
                    textTransform: 'uppercase',
                  }}
                />
              </div>
            </VibeTooltip>

          </div>
        )}
        style={{
          borderBottom: `1px solid ${color.whiteSmoke}`,
        }}
        buttons={getMenuItems()}
        isDropMenu
      />

      <div
        style={{
          display: 'flex',
          padding: 24,
        }}
      >
        <OrderStatItem
          title="Total Impressions"
          value={orderData.totalImpressions || 0}
        />

        <OrderStatItem
          title="Target Impressions"
          value={orderData.targetImpressions || 0}
        />

        <OrderStatItem
          title="Total Spots"
          value={orderData.totalSpots || 0}
        />

        <OrderStatItem
          title="Total Budget"
          value={orderData.budget || 0}
          currency
        />

        <OrderStatItem
          title="Weighted CPM"
          value={orderData.weightedCpm || 0}
          currency
        />
      </div>

      {user.can('segment.view') && (
        <TableSegments
          columns={[
            'Name',
            'Media Format',
            'Segment Type',
            'Start Date',
            'End Date',
            'Start Time',
            'End Time',
            'Budget',
            'CPM',
            'Target Impressions',
            'Total Impressions',
            'Spots',
            'Frequency',
            'Spot Type',
            'Spot Selection Strategy',
            'Days of Week',
            'Created By',
            'Created Date',
            '...',
          ]}
          collection={orderSegments}
          defaultSortBy={{
            label: 'Name',
            attr: 'name',
            direction: 'asc',
          }}
          paginator
          paginatorProps={{
            label: 'Segments',
            urlPaging: true,
            urlFilters: true,
          }}
          rowLink={{
            segmentId: '_id',
          }}
          // include segment summary data to show warnings and rollup information
          segmentSummaries={orderSegments}
        />
      )}

      {/* Copy an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmCopy}
        confirmProps={{
          text: 'Copy Order',
          color: color.violetVibe,
        }}
        cancelProps={{
          text: 'Cancel',
          color: color.manatee,
        }}
        title="Copy Order"
        text={`Are you sure you want to copy ${orderData.name}?`}
        onConfirm={onConfirmCopy}
        onClose={onCancelCopy}
      />

      {/* Pause an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmPause}
        confirmProps={{
          text: 'Pause Order',
          color: color.violetVibe,
          disabled: isEmpty(statusReason),
        }}
        cancelProps={{
          text: 'Cancel',
          color: color.manatee,
        }}
        title="Pause Order"
        text={(
          <div>
            <div
              style={{
                marginBottom: 16,
              }}
            >
              Are you sure you want to pause {orderData.name}?
            </div>

            <Field
              type="textarea"
              style={{
                height: 150,
              }}
              label="Reason"
              placeholder="Reason for Pausing..."
              value={statusReason}
              onChange={onChangeStatusReason}
              autoFocus
              required
            />
          </div>
        )}
        onConfirm={onConfirmPause}
        onClose={onCancelPause}
      />

      {/* Resume an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmResume}
        confirmProps={{
          text: 'Resume Order',
          color: color.violetVibe,
        }}
        cancelProps={{
          text: 'Cancel',
          color: color.manatee,
        }}
        title="Resume Order"
        text={`Are you sure you want to resume ${orderData.name}?`}
        onConfirm={onConfirmResume}
        onClose={onCancelResume}
      />

      {/* Cancel an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmCancel}
        confirmProps={{
          text: 'Cancel Order',
          color: color.violetVibe,
          disabled: isEmpty(statusReason),
        }}
        cancelProps={{
          text: 'Cancel',
          color: color.manatee,
        }}
        title="Cancel Order"
        text={(
          <div>
            <div
              style={{
                marginBottom: 16,
              }}
            >
              Are you sure you want to cancel {orderData.name}?
            </div>

            <Field
              type="textarea"
              style={{
                height: 150,
              }}
              label="Reason"
              placeholder="Reason for Canceling..."
              value={statusReason}
              onChange={onChangeStatusReason}
              autoFocus
              required
            />
          </div>
        )}
        onConfirm={onConfirmCancel}
        onClose={onCancelCancel}
      />
    </div>
  );
}

OrderDetails.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
};

OrderDetails.defaultProps = {
  className: '',
  style: {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetails);
